// 공용 포매팅 헬퍼.

import type { GameSummary, Level, RequestType, Source } from '@/api/types';

export function levelLabel(level: Level): string {
  return level === 'editor' ? 'Editor' : 'Viewer';
}

export function stageName(stage: string): string {
  return stage === 'UA_OPS' ? 'UA Ops' : `Stage ${stage.replace('STAGE_', '')}`;
}

// stage_grant 요청 payload.changes → [{ stage, level|null }]. 모르는 형태는 빈 배열.
export function stageGrantChanges(payload: Record<string, unknown> | null | undefined): { stage: string; level: Level | null }[] {
  const raw = payload?.changes;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const c = item as { stage?: unknown; level?: unknown };
    if (typeof c?.stage !== 'string') return [];
    return [{ stage: c.stage, level: c.level === 'editor' || c.level === 'viewer' ? c.level : null }];
  });
}

export function sourceLabel(source: Source): string {
  switch (source) {
    case 'request':
      return '요청 승인';
    case 'auto_expand':
      return '자동 확장';
    case 'migration':
      return '마이그레이션';
    case 'admin_direct':
      return 'Admin 즉시 부여';
    default:
      return source;
  }
}

// 네트워크 메타데이터 반영 요청은 payload.kind 로 무엇을 바꾸는지 갈린다.
export function networkMetaKindLabel(kind: string | undefined): string {
  if (kind === 'create') return '네트워크 추가';
  if (kind === 'policy') return '그룹 판정 변경';
  if (kind === 'admob') return 'AdMob ad source 반영 (없어진 기능)';
  return '설정 변경';
}

// 리셀러 허용 반영 요청은 payload.kind 로 이메일 행을 만들지·고칠지·지울지가 갈린다.
export function resellerAllowedKindLabel(kind: string | undefined): string {
  if (kind === 'create') return '이메일 추가';
  if (kind === 'bulk_create') return '이메일 여러 개 추가';
  if (kind === 'delete') return '이메일 삭제';
  return '보는 범위 변경';
}

export function requestTypeLabel(t: RequestType): string {
  switch (t) {
    case 'grant':
      return '권한 부여 요청';
    case 'revoke':
      return '권한 회수 요청';
    case 'project_create':
      return '프로젝트 생성 요청';
    case 'project_edit':
      return '프로젝트 정보 수정 요청';
    case 'api_token':
      return 'API 토큰 발급 요청';
    case 'network_meta_apply':
      return 'Admon 네트워크 반영 요청';
    case 'reseller_allowed_apply':
      return '리셀러 허용 반영 요청';
    case 'role_member':
      return 'Orbit 가입(멤버 권한) 요청';
    case 'stage_grant':
      return 'Stage 권한 요청';
    default:
      return t;
  }
}

const TZ_OFFSET_HOURS = 9;

// ISO timestamp → "2026-05-01 10:00" 표시. timezone은 KST 가정.
export function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const utc = d.getTime() + d.getTimezoneOffset() * 60 * 1000;
  const local = new Date(utc + TZ_OFFSET_HOURS * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())} ${pad(local.getHours())}:${pad(local.getMinutes())}`;
}

// 짧은 표시. "2026-05-01"
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const utc = d.getTime() + d.getTimezoneOffset() * 60 * 1000;
  const local = new Date(utc + TZ_OFFSET_HOURS * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`;
}

// "ocean@supercent.io" → "ocean"
export function emailLocal(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.indexOf('@');
  return at === -1 ? email : email.slice(0, at);
}

// 표시용 — display_name이 없으면 prefix 폴백.
export function projectLabel(display_name: string | null, prefix: string): string {
  return display_name && display_name.trim().length > 0 ? display_name : prefix;
}

// 게임 id/prefix → 게임명 맵 (meta.games 기반). project_id·prefix 둘 다 키로 등록해
// 요청의 game_prefixes가 어느 형식이든 매칭되게 한다.
export function buildGamesById(games: GameSummary[]): Record<string, string> {
  const m: Record<string, string> = {};
  for (const g of games) {
    const name = g.display_name ?? '';
    if (!name) continue;
    if (g.project_id) m[g.project_id] = name;
    if (g.prefix) m[g.prefix] = name;
  }
  return m;
}

// 게임 id/prefix를 "게임명 (id)"로 표시. 메타에 이름이 없으면 id 그대로.
export function gameLabel(idOrPrefix: string, gamesById: Record<string, string>): string {
  const name = gamesById[idOrPrefix];
  return name ? `${name} (${idOrPrefix})` : idOrPrefix;
}
